import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type DragEvent as ReactDragEvent,
} from 'react'
import { Check, Loader2, Search, Upload } from 'lucide-react'
import { cn } from '../../lib/utils'
import { Button } from '../../components/button'
import { Dialog, DialogContent, DialogTitle } from '../../components/dialog'
import { Input } from '../../components/input'
import type { Asset, AssetGroup, AssetPickerKind } from './types'

export interface AssetPickerProps {
  open: boolean
  onOpenChange: (open: boolean) => void

  /** Flat list of assets to choose from. Use `groups` instead if
   * you want them split into labelled sections. Pass at least
   * one. */
  assets?: Asset[]
  groups?: AssetGroup[]

  /** Pagination — call when the user reaches the bottom of the
   * grid. Don't fire when `hasMore` is false or `isLoading` is
   * true; the picker also debounces on its own. */
  hasMore?: boolean
  onLoadMore?: () => void
  isLoading?: boolean

  /** Search-as-you-type. Controlled. If omitted, the search bar
   * is hidden. */
  search?: string
  onSearchChange?: (search: string) => void

  /** New-upload pipeline. Files are dropped into the embedded
   * dropzone OR picked via the system file dialog. Resolve with
   * the freshly-uploaded asset(s) so the host can prepend them
   * to its assets list. Reject (or throw) on failure — the
   * picker surfaces a small error chip. */
  onUpload?: (files: File[]) => Promise<Asset[] | void>
  isUploading?: boolean
  /** Optional progress 0–100 for the bar shown while uploading. */
  uploadProgress?: number

  /** Selection. `multi` toggles single vs multi-select; default
   * single. Selected assets light up; the footer's Use button
   * resolves with them. */
  multi?: boolean
  /** Initial / controlled selection. Both single + multi accept
   * an array (length 0–1 in single mode). */
  selectedIds?: string[]
  onSelectedChange?: (ids: string[]) => void
  /** Confirm selection. Called when the user clicks Use, or
   * double-clicks a tile (which fires onSelect with that single
   * asset regardless of `multi`). The host typically resolves
   * its own picker promise from inside this. */
  onSelect: (assets: Asset[]) => void

  /** Restrict what kind of files the dropzone + system picker
   * accept. Default `image` (most common). */
  kind?: AssetPickerKind

  /** Explicit allow-list of mime types. When provided overrides
   * the `kind` default. Files outside the list are blocked at
   * the picker (never reach `onUpload`) and the operator sees an
   * inline warning naming each rejected file. Default for
   * `kind="image"` is JPEG / PNG / GIF / WEBP — the four formats
   * supported by every downstream we care about (Anthropic
   * vision, S3 thumbnailers, marketing email clients). AVIF /
   * SVG / HEIC etc. are deliberately NOT in the default — those
   * cause the hard "unsupported file format" error from
   * Anthropic. Pass your own list to widen / narrow as the
   * host's pipeline allows. */
  acceptedMimeTypes?: string[]

  /** Title rendered in the dialog header. Default "Pick an
   * asset". */
  title?: string
  /** Optional supporting copy under the title. */
  description?: string
  /** Use button label. Default "Use selected". */
  confirmLabel?: string

  /** Extra controls rendered in the toolbar, before the Upload button — e.g.
   * a folder selector that decides where new uploads land. */
  toolbarSlot?: React.ReactNode

  /** Optional left-hand rail (Finder-style) rendered beside the grid — e.g. a
   * folder list. The host filters the `assets`/`groups` it passes to match. */
  sidebar?: React.ReactNode

  className?: string
}

const ACCEPT_BY_KIND: Record<AssetPickerKind, string> = {
  // Match the default mime allow-list below, not raw `image/*` —
  // otherwise the OS file picker happily lets the operator
  // select an AVIF / SVG, which we'd then reject inline. Showing
  // only acceptable types in the picker is faster + clearer.
  image: 'image/jpeg,image/png,image/gif,image/webp',
  any: '*/*',
}

/** Default allow-list for `kind="image"`. The four formats every
 * downstream we currently care about supports (Anthropic vision,
 * S3 thumbnailers, marketing email clients). Override via
 * `acceptedMimeTypes` to widen / narrow. */
const DEFAULT_IMAGE_MIMES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
]

interface RejectedFile {
  filename: string
  mimeType: string
}

/**
 * Tailwind-v4 asset picker — replaces ad-hoc gallery modals
 * across the new UI (email-builder image picker, agent-chat
 * attachment picker, etc.) with one consistent surface.
 *
 * Headless: the host owns the asset list, search query, and
 * upload pipeline. Pass data + callbacks; the picker handles
 * grid rendering, selection state, drag-drop, and the dialog
 * shell. Resolves via `onSelect` when the user confirms.
 *
 * Layout:
 *   ┌──────────────────────────────────────────────┐
 *   │ Title                                  [X]   │
 *   │ description (optional)                       │
 *   ├──────────────────────────────────────────────┤
 *   │ [search]              [Upload] [Use]         │
 *   ├──────────────────────────────────────────────┤
 *   │ ┌────┐ ┌────┐ ┌────┐ ┌────┐                  │
 *   │ │ □  │ │ □  │ │ ✓  │ │ □  │  ← selectable    │
 *   │ └────┘ └────┘ └────┘ └────┘                  │
 *   │ … paginated, drag-drop active overlay        │
 *   └──────────────────────────────────────────────┘
 */
export function AssetPicker({
  open,
  onOpenChange,
  assets,
  groups,
  hasMore,
  onLoadMore,
  isLoading,
  search,
  onSearchChange,
  onUpload,
  isUploading,
  uploadProgress,
  multi = false,
  selectedIds: controlledSelectedIds,
  onSelectedChange,
  onSelect,
  kind = 'image',
  acceptedMimeTypes,
  title = 'Pick an asset',
  description,
  confirmLabel = 'Use selected',
  toolbarSlot,
  sidebar,
  className,
}: AssetPickerProps) {
  const titleId = useId()
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const scrollRef = useRef<HTMLDivElement | null>(null)

  // Effective mime allow-list. Explicit prop wins; otherwise
  // derive from `kind`. `null` = no restriction (all files
  // allowed — `kind="any"` without an explicit list).
  const effectiveAllowList = useMemo<string[] | null>(() => {
    if (acceptedMimeTypes && acceptedMimeTypes.length > 0) {
      return acceptedMimeTypes
    }
    if (kind === 'image') return DEFAULT_IMAGE_MIMES
    return null
  }, [acceptedMimeTypes, kind])

  // Inline rejection warning. Shown until the next successful
  // upload OR the user dismisses it.
  const [rejected, setRejected] = useState<RejectedFile[]>([])
  const dismissRejected = useCallback(() => setRejected([]), [])

  const partitionFiles = useCallback(
    (files: File[]): { accepted: File[]; rejected: RejectedFile[] } => {
      if (!effectiveAllowList) return { accepted: files, rejected: [] }
      const accepted: File[] = []
      const rej: RejectedFile[] = []
      for (const f of files) {
        if (effectiveAllowList.includes(f.type)) {
          accepted.push(f)
        } else {
          rej.push({ filename: f.name, mimeType: f.type || 'unknown' })
        }
      }
      return { accepted, rejected: rej }
    },
    [effectiveAllowList]
  )

  // Selection state — supports both controlled (host passes
  // `selectedIds` + `onSelectedChange`) and uncontrolled (picker
  // tracks internally, only fires on confirm).
  const [internalSelected, setInternalSelected] = useState<string[]>([])
  const isControlled = controlledSelectedIds !== undefined
  const selectedIds = isControlled ? controlledSelectedIds : internalSelected
  const setSelectedIds = useCallback(
    (next: string[]) => {
      if (!isControlled) setInternalSelected(next)
      onSelectedChange?.(next)
    },
    [isControlled, onSelectedChange]
  )

  // Reset internal selection when the picker reopens — opening
  // it should always start from a clean state for the user, even
  // if they cancelled with stuff selected last time.
  useEffect(() => {
    if (!open) return
    if (!isControlled) setInternalSelected([])
  }, [open, isControlled])

  // Flatten groups into one consultable list for selection
  // resolution + double-click. Preserves grouping for rendering.
  const flatAssets = useMemo(() => {
    if (assets) return assets
    if (groups) return groups.flatMap((g) => g.assets)
    return []
  }, [assets, groups])

  const findAsset = useCallback(
    (id: string) => flatAssets.find((a) => a.id === id),
    [flatAssets]
  )

  const toggleSelect = useCallback(
    (id: string) => {
      if (multi) {
        setSelectedIds(
          selectedIds.includes(id)
            ? selectedIds.filter((x) => x !== id)
            : [...selectedIds, id]
        )
      } else {
        setSelectedIds([id])
      }
    },
    [multi, selectedIds, setSelectedIds]
  )

  const confirmSelection = useCallback(() => {
    const picked = selectedIds
      .map(findAsset)
      .filter((a): a is Asset => Boolean(a))
    if (picked.length === 0) return
    onSelect(picked)
    onOpenChange(false)
  }, [selectedIds, findAsset, onSelect, onOpenChange])

  // Drag-drop overlay state. Tracks dragOver depth so leaving a
  // child element doesn't prematurely deactivate the overlay.
  const dragDepthRef = useRef(0)
  const [isDragOver, setIsDragOver] = useState(false)
  const onDragEnter = useCallback(
    (e: ReactDragEvent<HTMLDivElement>) => {
      if (!onUpload) return
      e.preventDefault()
      dragDepthRef.current += 1
      setIsDragOver(true)
    },
    [onUpload]
  )
  const onDragLeave = useCallback(
    (e: ReactDragEvent<HTMLDivElement>) => {
      if (!onUpload) return
      e.preventDefault()
      dragDepthRef.current = Math.max(0, dragDepthRef.current - 1)
      if (dragDepthRef.current === 0) setIsDragOver(false)
    },
    [onUpload]
  )
  const onDragOver = useCallback(
    (e: ReactDragEvent<HTMLDivElement>) => {
      if (!onUpload) return
      e.preventDefault()
    },
    [onUpload]
  )
  const onDrop = useCallback(
    async (e: ReactDragEvent<HTMLDivElement>) => {
      if (!onUpload) return
      e.preventDefault()
      dragDepthRef.current = 0
      setIsDragOver(false)
      const files = Array.from(e.dataTransfer?.files ?? [])
      if (files.length === 0) return
      const { accepted, rejected: rej } = partitionFiles(files)
      if (rej.length > 0) setRejected(rej)
      if (accepted.length === 0) return
      try {
        await onUpload(accepted)
        // On success, clear any prior rejection banner — the
        // operator has moved on.
        if (rej.length === 0) setRejected([])
      } catch {
        /* host surfaces error via `isUploading=false` + state */
      }
    },
    [onUpload, partitionFiles]
  )

  const handleSystemFiles = useCallback(
    async (files: File[]) => {
      if (!onUpload || files.length === 0) return
      const { accepted, rejected: rej } = partitionFiles(files)
      if (rej.length > 0) setRejected(rej)
      if (accepted.length === 0) return
      try {
        await onUpload(accepted)
        if (rej.length === 0) setRejected([])
      } catch {
        /* host surfaces error */
      }
    },
    [onUpload, partitionFiles]
  )

  // Infinite scroll — fire onLoadMore when the scroll container
  // is within ~80px of the bottom and we're not already loading.
  useEffect(() => {
    const el = scrollRef.current
    if (!el || !onLoadMore || !hasMore) return
    const handler = () => {
      if (isLoading) return
      const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80
      if (nearBottom) onLoadMore()
    }
    el.addEventListener('scroll', handler, { passive: true })
    return () => el.removeEventListener('scroll', handler)
  }, [onLoadMore, hasMore, isLoading])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        aria-labelledby={titleId}
        className={cn(
          'flex h-[85dvh] w-[min(960px,92vw)] !max-w-none flex-col gap-0 overflow-hidden p-0',
          className
        )}
      >
        {/* Header */}
        <div className="flex shrink-0 items-start gap-3 border-b border-black/5 px-5 py-4 dark:border-white/5">
          <div className="min-w-0 flex-1">
            <DialogTitle id={titleId} className="text-base font-medium">
              {title}
            </DialogTitle>
            {description && (
              <p className="mt-0.5 text-sm text-muted-foreground">
                {description}
              </p>
            )}
          </div>
        </div>

        {/* Toolbar */}
        <div className="flex shrink-0 items-center gap-2 border-b border-black/5 px-5 py-3 dark:border-white/5">
          {onSearchChange && (
            <div className="relative min-w-0 flex-1">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
                aria-hidden="true"
              />
              <Input
                value={search ?? ''}
                onChange={(e) => onSearchChange(e.target.value)}
                placeholder="Search assets…"
                className="pl-9"
              />
            </div>
          )}
          {toolbarSlot}
          <input
            ref={fileInputRef}
            type="file"
            multiple={multi}
            accept={
              effectiveAllowList
                ? effectiveAllowList.join(',')
                : ACCEPT_BY_KIND[kind]
            }
            className="sr-only"
            onChange={(e) => {
              const files = Array.from(e.target.files ?? [])
              if (files.length > 0) void handleSystemFiles(files)
              e.currentTarget.value = ''
            }}
          />
          {onUpload && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              leftIcon={
                isUploading ? <Loader2 className="animate-spin" /> : <Upload />
              }
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
            >
              {isUploading ? 'Uploading…' : 'Upload'}
            </Button>
          )}
          <Button
            type="button"
            variant="primary"
            size="sm"
            leftIcon={<Check />}
            onClick={confirmSelection}
            disabled={selectedIds.length === 0}
          >
            {confirmLabel}
            {multi && selectedIds.length > 1 ? ` (${selectedIds.length})` : ''}
          </Button>
        </div>

        {/* Optional upload progress bar — pinned under the toolbar */}
        {isUploading && typeof uploadProgress === 'number' && (
          <div className="h-0.5 shrink-0 bg-foreground/5">
            <div
              className="h-full bg-foreground transition-[width] duration-150 ease-out"
              style={{
                width: `${Math.max(0, Math.min(100, uploadProgress))}%`,
              }}
            />
          </div>
        )}

        {/* Rejection banner — files dropped or picked that don't
            match the allow-list never reach `onUpload`; the
            operator gets a clear in-context warning naming each
            file so they know to convert before retrying. */}
        {rejected.length > 0 && (
          <div
            role="alert"
            className="flex shrink-0 items-start gap-2 border-b border-amber-500/20 bg-amber-500/10 px-5 py-2.5 text-xs"
          >
            <div className="min-w-0 flex-1 text-foreground">
              <p className="font-medium">
                {rejected.length === 1
                  ? "1 file isn't supported"
                  : `${rejected.length} files aren't supported`}
                {effectiveAllowList && (
                  <>
                    {' '}
                    — accepted formats:{' '}
                    <span className="font-mono">
                      {effectiveAllowList
                        .map((m) => m.replace(/^image\//, '.'))
                        .join(', ')}
                    </span>
                  </>
                )}
              </p>
              <ul className="mt-1 space-y-0.5 text-muted-foreground">
                {rejected.slice(0, 4).map((r, i) => (
                  <li key={i} className="truncate">
                    {r.filename}{' '}
                    <span className="opacity-70">({r.mimeType})</span>
                  </li>
                ))}
                {rejected.length > 4 && (
                  <li className="opacity-70">
                    …and {rejected.length - 4} more
                  </li>
                )}
              </ul>
              <p className="mt-1 text-[0.7rem] text-muted-foreground">
                Convert to one of the accepted formats and try again.
              </p>
            </div>
            <button
              type="button"
              onClick={dismissRejected}
              aria-label="Dismiss warning"
              className="shrink-0 rounded text-muted-foreground hover:text-foreground"
            >
              ×
            </button>
          </div>
        )}

        {/* Body — optional Finder-style sidebar on the left, grid on the right */}
        <div className="flex min-h-0 flex-1">
          {sidebar ? (
            <aside className="w-52 shrink-0 overflow-y-auto border-r border-black/5 dark:border-white/5">
              {sidebar}
            </aside>
          ) : null}
          <div
            ref={scrollRef}
            className="relative min-h-0 flex-1 overflow-y-auto overscroll-contain"
            onDragEnter={onDragEnter}
            onDragLeave={onDragLeave}
            onDragOver={onDragOver}
            onDrop={onDrop}
          >
            <div className="px-5 py-4">
              {groups ? (
                <div className="flex flex-col gap-6">
                  {groups.map((g) => (
                    <section key={g.id}>
                      <h3 className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                        {g.label}
                      </h3>
                      <AssetGrid
                        assets={g.assets}
                        selectedIds={selectedIds}
                        onToggle={toggleSelect}
                        onConfirmOne={(asset) => {
                          onSelect([asset])
                          onOpenChange(false)
                        }}
                      />
                    </section>
                  ))}
                </div>
              ) : (
                <AssetGrid
                  assets={assets ?? []}
                  selectedIds={selectedIds}
                  onToggle={toggleSelect}
                  onConfirmOne={(asset) => {
                    onSelect([asset])
                    onOpenChange(false)
                  }}
                />
              )}

              {(isLoading || hasMore) && (
                <div className="flex items-center justify-center py-6 text-xs text-muted-foreground">
                  {isLoading ? (
                    <>
                      <Loader2
                        className="mr-1.5 size-3.5 animate-spin"
                        aria-hidden="true"
                      />
                      Loading…
                    </>
                  ) : (
                    <button
                      type="button"
                      onClick={onLoadMore}
                      className="hover:text-foreground"
                    >
                      Load more
                    </button>
                  )}
                </div>
              )}

              {!isLoading && flatAssets.length === 0 && (
                <div className="flex flex-col items-center justify-center gap-2 py-16 text-sm text-muted-foreground">
                  <p>No assets yet.</p>
                  {onUpload && (
                    <p className="text-xs">
                      Drop a file here or click <strong>Upload</strong> to add
                      one.
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Drop overlay */}
            {isDragOver && onUpload && (
              <div className="pointer-events-none absolute inset-0 z-10 grid place-items-center bg-primary/5 backdrop-blur-sm">
                <div className="flex flex-col items-center gap-2 rounded-2xl border-2 border-dashed border-primary/40 bg-background/80 px-8 py-6 text-sm text-foreground shadow-lg">
                  <Upload className="size-6 text-primary" aria-hidden="true" />
                  <span>Drop to upload</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

interface AssetGridProps {
  assets: Asset[]
  selectedIds: string[]
  onToggle: (id: string) => void
  onConfirmOne: (asset: Asset) => void
}

function AssetGrid({
  assets,
  selectedIds,
  onToggle,
  onConfirmOne,
}: AssetGridProps) {
  if (assets.length === 0) return null
  return (
    <ul className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
      {assets.map((a) => {
        const isSelected = selectedIds.includes(a.id)
        const isImage = a.mimeType.startsWith('image/')
        const isVideo = a.mimeType.startsWith('video/')
        const previewUrl = a.thumbnailUrl ?? (isImage ? a.url : undefined)
        return (
          <li key={a.id}>
            <button
              type="button"
              role="checkbox"
              aria-checked={isSelected}
              aria-label={a.filename}
              onClick={() => onToggle(a.id)}
              onDoubleClick={() => onConfirmOne(a)}
              // Inline aspect-ratio as a fallback: some host Tailwind bridges
              // (e.g. connect) don't emit `aspect-square`, collapsing the tile
              // to a thin line. Native CSS guarantees the square height.
              style={{ aspectRatio: '1 / 1' }}
              className={cn(
                // Tile is a positioned block container (no flex)
                // — keeps the image's `absolute inset-0` filling
                // the box reliably regardless of natural aspect.
                'group relative block aspect-square w-full overflow-hidden rounded-xl border bg-foreground/[0.02] text-left transition',
                // Subtle resting border, deepens on hover. Solid
                // foreground when selected — same neutral palette
                // the rest of the new UI uses (no `ring`, no
                // primary token which can resolve to dark in
                // host bridges).
                'border-black/10 dark:border-white/10',
                'hover:border-black/20 dark:hover:border-white/20',
                isSelected && 'border-foreground hover:border-foreground'
              )}
            >
              {previewUrl ? (
                <img
                  src={previewUrl}
                  alt={a.filename}
                  // `absolute inset-0` guarantees the image fills
                  // the aspect-square tile — `size-full` alone
                  // doesn't reliably work inside flex / grid
                  // children with implicit aspect-ratio heights.
                  className="absolute inset-0 size-full object-cover"
                  loading="lazy"
                />
              ) : isVideo ? (
                // Still preview: metadata preload paints the first frame,
                // no autoplay (design law: no ambient motion).
                <video
                  src={a.url}
                  muted
                  playsInline
                  preload="metadata"
                  className="pointer-events-none absolute inset-0 size-full object-cover"
                />
              ) : (
                <div className="absolute inset-0 grid place-items-center text-xs text-muted-foreground">
                  {a.mimeType.split('/')[1] ?? 'file'}
                </div>
              )}

              {/* Selected-state tint overlay — soft darken so the
                  selection reads even on light images. */}
              {isSelected && (
                <span
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-0 bg-foreground/10"
                />
              )}

              {isSelected && (
                <span className="pointer-events-none absolute right-1.5 top-1.5 grid size-5 place-items-center rounded-full bg-foreground text-background shadow">
                  <Check className="size-3" aria-hidden="true" />
                </span>
              )}
            </button>
            {/* Always-visible caption so files (e.g. a PDF shown as an icon)
                are identifiable without hovering. */}
            <p
              className="mt-1 truncate px-0.5 text-[11px] leading-tight text-foreground"
              title={a.filename}
            >
              {a.filename}
            </p>
          </li>
        )
      })}
    </ul>
  )
}
